import { AddComponentDto, AddItemDto, AddItemOptionDto, CartAdditionOption } from '../../types/cart';
import { MenuSectionDto } from '../../generated-api/models/MenuSectionDto';
import { MenuItemVariantDto } from '../../generated-api/models/MenuItemVariantDto';
import { MenuBuilderComponentDto } from '../../generated-api/models/MenuBuilderComponentDto';
import { OptionDto } from '../../generated-api/models/OptionDto';
import Big from 'big.js';
import {
  DeliveryMethodDto,
  DiscountDto,
  PayCartDto,
  PaymentMethodDto,
  OrderLineDto,
  OrderLineOptionDto,
  CartDto,
} from '../../generated-api';

export const itemIsEqualToOrderLine = (item: AddItemDto, orderLine: OrderLineDto) => {
  if (item.itemVariantId === orderLine.itemVariantId) {
    return (
      item.comment === orderLine.comment &&
      item.options.length === orderLine.options.length &&
      item.options.every((option) => existIn(option, orderLine.options))
    );
  }

  return false;
};

const existIn = (additionOption: AddItemOptionDto, orderLineOptions: OrderLineOptionDto[]) =>
  orderLineOptions.some((olOption) => isEqualOption(additionOption, olOption));

const isEqualOption = (option: AddItemOptionDto, orderLineOption: OrderLineOptionDto) =>
  orderLineOption.itemVariantId === option.itemVariantId && orderLineOption.quantity === option.quantity;

export const isEqualNotOptionId = (option?: OptionDto) => (cartOption: CartAdditionOption) =>
  cartOption.option.optionId !== option?.optionId;
export const isEqualOptionId = (option?: OptionDto) => (cartOption: CartAdditionOption) =>
  cartOption.option.optionId === option?.optionId;

export const getAllItemsFromSections = (sections: MenuSectionDto[]): MenuItemVariantDto[] =>
  sections.flatMap((section) => [...section.items, ...section.subsections.flatMap((sub) => sub.items)]);

export const findMenuItemByOrderLine = (
  orderLine: OrderLineDto,
  sections: MenuSectionDto[]
): MenuItemVariantDto | undefined =>
  getAllItemsFromSections(sections).find((item) => item.variantLinkId === orderLine.itemVariantId);

export const isCartAdditionOption = (option: unknown): option is CartAdditionOption =>
  Boolean(option && (option as CartAdditionOption).option && (option as CartAdditionOption).type);

export const calcTotalServiceCharge = (price: number, serviceCharge?: number) =>
  serviceCharge ? Number(new Big(serviceCharge).div(100).times(price)) : 0;

type PayCartInfo = {
  paymentMethod: PaymentMethodDto;
  deliveryMethod: DeliveryMethodDto;
  deviceId: string;
  tableNumber?: string;
  tipAmount: number;
  comment: string | undefined;
  placementId?: number;
  discount?: DiscountDto;
  dynamicFields?: { [key: string]: string };
  giftCardPhoneNumber?: string;
  vatType: 'EAT_IN' | 'TAKEAWAY';
};

export const buildAddComponentDto = (
  componentDto: MenuBuilderComponentDto,
  allProducts: MenuItemVariantDto[],
  componentCustomizations: Map<string, CartAdditionOption[]>,
  parentMenuSectionId?: string,
  parentSectionProductId?: string
): AddComponentDto => {
  const resolvedItem = allProducts.find((p) => p.variantLinkId === componentDto.productSizeId);
  const options = componentCustomizations.get(componentDto.productSizeId) ?? [];

  const subComponents =
    resolvedItem?.isComposite && resolvedItem.components
      ? resolvedItem.components.map((sub) =>
          buildAddComponentDto(sub, allProducts, componentCustomizations, parentMenuSectionId, parentSectionProductId)
        )
      : undefined;

  return {
    itemVariantId: componentDto.productSizeId,
    name: componentDto.productName,
    price: componentDto.effectivePrice,
    takeawayPrice: componentDto.effectiveTakeawayPrice,
    menuSectionId: parentMenuSectionId,
    sectionProductId: parentSectionProductId,
    unitQuantity: componentDto.quantity,
    courseId: resolvedItem?.courseId,
    options: options.map((opt) => ({
      name: opt.option.name,
      itemVariantId: opt.option.optionId,
      type: opt.type,
      quantity: opt.quantity,
      price: opt.option.price,
      takeawayPrice: opt.option.takeawayPrice,
    })),
    components: subComponents,
  };
};

export const toPayCartDto = (payCartInfo: PayCartInfo, cart: CartDto): PayCartDto => ({
  tipAmount: payCartInfo.tipAmount,
  deliveryMethodId: payCartInfo.deliveryMethod.id,
  deliveryPoint: {
    primaryReference: payCartInfo.tableNumber || payCartInfo.deliveryMethod.primaryReference || '',
  },
  comment: payCartInfo.comment || undefined,
  placementId: payCartInfo.placementId,
  discountCode: payCartInfo.discount?.discountCodes[0],
  paymentMethod: payCartInfo.paymentMethod,
  deviceId: payCartInfo.deviceId,
  dynamicFields: payCartInfo.dynamicFields,
  giftCardPhoneNumber: payCartInfo.giftCardPhoneNumber,
  vatType: payCartInfo.vatType,
  cart: cart,
});
